import { useReducedMotion } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';
import { StarField, type StarsDensity, type StarsSpeed } from './StarField';
import { GradientOrbs } from './GradientOrbs';

/**
 * GlobalBackground — mounts the atmospheric layers behind every authenticated page.
 *
 * Layering (back to front):
 *   1. Page surface (var(--bg-surface)) — owned by parent
 *   2. GradientOrbs   (drifting sage radial blobs)
 *   3. StarField      (canvas with drifting white + accent particles)
 *   4. Page content   (zIndex: 1, owned by parent)
 *
 * Light mode: StarField hidden, GradientOrbs use deeper sage at lower opacity.
 */

interface GlobalBackgroundProps {
  density?:   StarsDensity;
  speed?:     StarsSpeed;
  heartbeat?: boolean;
}

export function GlobalBackground({
  density   = 'normal',
  speed     = 'slow',
  heartbeat = true,
}: GlobalBackgroundProps = {}) {
  const { resolved } = useTheme();
  const isLight = resolved === 'light';
  const reducedMotion = useReducedMotion();

  if (reducedMotion) return null;

  return (
    <div
      className="absolute inset-0"
      style={{ pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}
      aria-hidden="true"
    >
      {!isLight && (
        <>
          <GradientOrbs heartbeat={heartbeat} rgb={[113, 180, 112]} />
          <StarField density={density} speed={speed} accent="sage" size="normal" />
        </>
      )}
    </div>
  );
}
